import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import {
  HomeIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  UserCircleIcon,
  QrCodeIcon,
  KeyIcon,
  ArrowPathIcon,
  TruckIcon,
  PuzzlePieceIcon,
  ViewColumnsIcon,
  DocumentTextIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline'
import { PageTitleInfo, BreadcrumbItem } from '../../types/layout'

/**
 * Hook para obtener el título y breadcrumbs de la página actual
 */
export const usePageTitle = () => {
  const location = useLocation()

  const pageTitle = useMemo((): PageTitleInfo => {
    const path = location.pathname
    const pathSegments = path.split('/').filter(Boolean)

    // Si es la ruta raíz, mostrar Inicio
    if (path === '/home' || path === '/') {
      return {
        title: 'Inicio',
        icon: <HomeIcon className="h-5 w-5 mr-2" />,
      }
    }

    // Obtener el último segmento de la ruta
    const lastSegment = pathSegments[pathSegments.length - 1]

    const actionTitles: Record<string, string> = {
      new: 'Nuevo',
      edit: 'Editar',
      view: 'Ver',
      list: 'Lista de',
    }

    // Mapeo de secciones a títulos e iconos
    const sectionTitles: Record<
      string,
      { title: string; icon: JSX.Element }
    > = {
      orders: {
        title: 'Pedidos',
        icon: <BuildingStorefrontIcon className="h-5 w-5 mr-2" />,
      },
      products: {
        title: 'Producto',
        icon: <ArchiveBoxIcon className="h-5 w-5 mr-2" />,
      },
      errors: {
        title: 'Errores',
        icon: <ExclamationTriangleIcon className="h-5 w-5 mr-2" />,
      },
      settings: {
        title: 'Configuración',
        icon: <WrenchScrewdriverIcon className="h-5 w-5 mr-2" />,
      },
      profile: {
        title: 'Perfil',
        icon: <UserCircleIcon className="h-5 w-5 mr-2" />,
      },
      qr: {
        title: 'Códigos QR',
        icon: <QrCodeIcon className="h-5 w-5 mr-2" />,
      },
      users: {
        title: 'Usuarios',
        icon: <UserCircleIcon className="h-5 w-5 mr-2" />,
      },
      permissions: {
        title: 'Permisos',
        icon: <KeyIcon className="h-5 w-5 mr-2" />,
      },
      'verify-transactions': {
        title: 'Verificar Transacciones',
        icon: <KeyIcon className="h-5 w-5 mr-2" />,
      },
      'sync-homecenter': {
        title: 'Sincronizar Pedidos HC',
        icon: <ArrowPathIcon className="h-5 w-5 mr-2" />,
      },
      pending: {
        title: 'Crear & Asignar Envío',
        icon: <TruckIcon className="h-5 w-5 mr-2" />,
      },
      liquidar: {
        title: 'Cotizar Envío',
        icon: <TruckIcon className="h-5 w-5 mr-2" />,
      },
      cancelar: {
        title: 'Cancelar Envíos',
        icon: <ExclamationTriangleIcon className="h-5 w-5 mr-2" />,
      },
      erp: {
        title: 'ERP',
        icon: <PuzzlePieceIcon className="h-5 w-5 mr-2" />,
      },
      'comparative-table': {
        title: 'Tabla Comparativa',
        icon: <ViewColumnsIcon className="h-5 w-5 mr-2" />,
      },
      'remisiones-sku': {
        title: 'Remisiones por SKU',
        icon: <DocumentTextIcon className="h-5 w-5 mr-2" />,
      },
      'mercadolibre-token': {
        title: 'Token Mercado Libre',
        icon: <KeyIcon className="h-5 w-5 mr-2" />,
      },
      marketplace: {
        title: 'Productos Marketplace',
        icon: <ShoppingBagIcon className="h-5 w-5 mr-2" />,
      },
      cotizaciones: {
        title: 'Cotizaciones',
        icon: <ArchiveBoxIcon className="h-5 w-5 mr-2" />,
      },
      envios: {
        title: 'Envíos',
        icon: <TruckIcon className="h-5 w-5 mr-2" />,
      },
      fulfillment: {
        title: 'Fulfillment',
        icon: <ArchiveBoxIcon className="h-5 w-5 mr-2" />,
      },
    }

    // Si es una acción (new, edit, view)
    if (actionTitles[lastSegment]) {
      const section = pathSegments[pathSegments.length - 2]
      const sectionInfo = sectionTitles[section]
      return {
        title: `${actionTitles[lastSegment]} ${sectionInfo?.title || section}`,
        icon: sectionInfo?.icon || <ArchiveBoxIcon className="h-5 w-5 mr-2" />,
      }
    }

    // Si es una sección
    if (sectionTitles[lastSegment]) {
      return {
        title: sectionTitles[lastSegment].title,
        icon: sectionTitles[lastSegment].icon,
      }
    }

    // Por defecto
    return {
      title:
        lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, ' '),
      icon: <ArchiveBoxIcon className="h-5 w-5 mr-2" />,
    }
  }, [location.pathname])

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const pathnames = location.pathname.split('/').filter(Boolean)
    return pathnames.map((value, index) => {
      const to = `/${pathnames.slice(0, index + 1).join('/')}`
      let label = value.charAt(0).toUpperCase() + value.slice(1)

      // Traducción de las migas de pan
      const translations: Record<string, string> = {
        orders: 'Pedidos',
        pending: 'Pendientes',
        'verify-transactions': 'Verificar Transacciones',
        'entrega-pdv': 'Entrega PDV',
        'despachos-bodega': 'Despachos bodega',
        liquidar: 'Cotizar Envío',
        erp: 'ERP',
        'comparative-table': 'Tabla Comparativa',
        'remisiones-sku': 'Remisiones por SKU',
        'mercadolibre-token': 'Token Mercado Libre',
        marketplace: 'Productos Marketplace',
        cotizaciones: 'Cotizaciones',
        envios: 'Envíos',
        fulfillment: 'Fulfillment',
        products: 'Productos',
        settings: 'Configuración',
        users: 'Usuarios',
        permissions: 'Permisos',
        errors: 'Errores',
      }

      if (translations[value]) {
        label = translations[value]
      } else {
        // Capitalizar y reemplazar guiones
        label = value
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }

      return {
        to,
        label,
        isLast: index === pathnames.length - 1,
      }
    })
  }, [location.pathname])

  return {
    pageTitle,
    breadcrumbs,
  }
}

